import { useEffect, useState } from "react";
import {
  ArrowLeft,
  BookOpenText,
  Bot,
  Building2,
  ExternalLink,
  FileText,
  LoaderCircle,
  ScanText,
  UserRound,
} from "lucide-react";
import { AppLink, FeedbackBanner, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  CatalogPagination,
  CopyCanonicalLink,
  formatLegalDate,
  navigateBackToCatalog,
  preservedCatalogHistoryState,
} from "../components/LegalCatalogUi.jsx";
import { DoctrineCover } from "../components/DoctrineCover.jsx";
import { DoctrinePdfViewer } from "../components/DoctrinePdfViewer.jsx";
import { usePageMetadata } from "../lib/use-page-metadata.js";

const CONTENT_LIMIT = 1;

function pageFromSearch(search) {
  const value = Number(new URLSearchParams(search).get("page") || 1);
  return Number.isInteger(value) && value > 0 ? value : 1;
}

function openJustiniano(prompt) {
  window.dispatchEvent(new CustomEvent("justiniano:compose", {
    detail: { appId: "instrumenta", prompt },
  }));
}

export function DoctrineDetailPage({ slug, pageNumber = null }) {
  const api = useApi();
  const { path, search, navigate } = useRouter();
  const page = pageNumber || pageFromSearch(search);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("pdf");

  useEffect(() => {
    setRecord(null);
    setViewMode("pdf");
  }, [slug]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.getDoctrine(slug, { page, limit: CONTENT_LIMIT })
      .then((data) => {
        if (!active) return;
        const resolvedPage = Math.min(page, data.pagination.totalPages);
        const canonicalPath = `/doctrina/${data.slug}/pagina/${resolvedPage}`;
        if (path.replace(/\/$/, "") !== canonicalPath || search) {
          navigate(
            canonicalPath,
            {
              replace: true,
              state: preservedCatalogHistoryState(),
            },
          );
          return;
        }
        setRecord(data);
        if (!data.pdf?.available) setViewMode("text");
      })
      .catch((requestError) => {
        if (!active) return;
        setRecord(null);
        setError(requestError.message);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, navigate, page, path, search, slug]);

  usePageMetadata({
    title: record ? `${record.title} · Doctrina · Instrumenta` : null,
    description: record
      ? `${record.authors.join(", ") || "Autor no informado"}. ${record.publisher || "Catálogo doctrinario de Instrumenta"}.`
      : null,
    canonicalPath: record?.pageUrl,
  });

  if (loading && !record) {
    return (
      <div className="page legal-resource-page">
        <section className="legal-resource-loading">
          <LoaderCircle className="spin" size={23} /> Abriendo la obra…
        </section>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="page legal-resource-page">
        <button
          className="back-link"
          type="button"
          onClick={() => navigateBackToCatalog(navigate, "/doctrina")}
        >
          <ArrowLeft size={17} /> Volver al catálogo
        </button>
        <FeedbackBanner>{error || "No encontramos la obra solicitada."}</FeedbackBanner>
      </div>
    );
  }

  const goToPage = (nextPage) => navigate(
    `/doctrina/${record.slug}/pagina/${nextPage}`,
    { replace: true, state: preservedCatalogHistoryState() },
  );
  const requestedPage = Math.min(page, record.pagination.totalPages);
  const requestedPagination = {
    ...record.pagination,
    page: requestedPage,
  };

  return (
    <div className="page legal-resource-page">
      <button
        className="back-link"
        type="button"
        onClick={() => navigateBackToCatalog(navigate, "/doctrina")}
      >
        <ArrowLeft size={17} /> Volver al catálogo de doctrina
      </button>

      <article className="legal-resource-header doctrine-resource-header">
        <DoctrineCover
          api={api}
          cover={record.cover}
          title={record.title}
          authors={record.authors}
          edition={record.edition}
          seed={record.id}
          className="doctrine-detail-cover"
        />
        <div className="legal-resource-heading-copy">
          <span className="eyebrow">Doctrina · Ficha de obra</span>
          <h1>{record.title}</h1>
          {record.subtitle && <p>{record.subtitle}</p>}
          <div className="legal-resource-meta">
            <span><UserRound size={15} /> {record.authors.join(", ") || "Autor no informado"}</span>
            {record.publisher && <span><Building2 size={15} /> {record.publisher}</span>}
            {record.edition && <span><BookOpenText size={15} /> {record.edition}</span>}
            {record.publishedAt && <span>Publicada el {formatLegalDate(record.publishedAt)}</span>}
          </div>
        </div>
        <div className="legal-resource-actions">
          <CopyCanonicalLink path={record.pageUrl} />
          {record.source.url && (
            <a className="secondary-button" href={record.source.url} target="_blank" rel="noreferrer">
              <ExternalLink size={16} /> Fuente original externa
            </a>
          )}
          <button
            className="primary-button"
            type="button"
            onClick={() => openJustiniano(
              `Analizá esta página de la obra doctrinaria y citá los pasajes que utilices: ${record.citation}. Fuente Tribunalea: ${new URL(record.pageUrl, window.location.origin)}`,
            )}
          >
            <Bot size={16} /> Consultar con Justiniano
          </button>
        </div>
      </article>

      <section className="legal-resource-summary-grid">
        <div><span>Páginas indexadas</span><strong>{record.pageCount.toLocaleString("es-AR")}</strong></div>
        <div><span>Edición</span><strong>{record.edition || "No informada"}</strong></div>
        <div><span>Editorial o fuente</span><strong>{record.publisher || record.source.organization || "No informada"}</strong></div>
      </section>

      <section className={`legal-resource-content doctrine-resource-viewer is-${viewMode}`}>
        <header className="doctrine-resource-viewer-header">
          <div>
            <span className="eyebrow">Contenido de la obra</span>
            <h2>{viewMode === "pdf" ? "PDF original" : "Texto extraído"}</h2>
            <small>
              Página {requestedPage} de {record.pagination.totalPages}
            </small>
          </div>
          <div className="doctrine-view-tabs" role="tablist" aria-label="Formato de lectura">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "pdf"}
              className={viewMode === "pdf" ? "is-active" : ""}
              disabled={!record.pdf?.available}
              onClick={() => setViewMode("pdf")}
              title={record.pdf?.available ? "Ver el documento original" : "El PDF original no está disponible"}
            >
              <FileText size={15} /> PDF original
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "text"}
              className={viewMode === "text" ? "is-active" : ""}
              onClick={() => setViewMode("text")}
            >
              <ScanText size={15} /> Texto extraído
            </button>
          </div>
        </header>
        {viewMode === "pdf" && record.pdf?.available ? (
          <DoctrinePdfViewer
            api={api}
            pdf={record.pdf}
            title={record.title}
            pageNumber={requestedPage}
          />
        ) : (
          <div className="legal-resource-sections">
            {record.content.map((item) => (
              <article key={item.id} id={item.pageNumber ? `pagina-${item.pageNumber}` : undefined}>
                <div className="legal-section-heading">
                  <span className="legal-section-reference">
                    {item.reference || `Página ${item.pageNumber || "sin número"}`}
                  </span>
                  <AppLink className="legal-direct-source" to={item.url}>
                    Abrir fuente Tribunalea <ExternalLink size={12} />
                  </AppLink>
                </div>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        )}
        <CatalogPagination
          pagination={requestedPagination}
          onPage={goToPage}
          itemLabel={record.contentKind === "pages" ? "páginas" : "fragmentos"}
          ariaLabel="Páginas de la obra doctrinaria"
        />
      </section>
    </div>
  );
}
